import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Demographics,
} from 'views/EMR/components/Insurance/Dialog/InsuranceForm';
import {
  InsuranceDTO,
} from 'dtos/insuranceDTO';
import {
  PatientDTO,
} from 'dtos/patientDTO';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import {
  InsuranceSlot,
} from 'dtos/insuranceSlot';
import {
  useDeleteInsurance,
  useInsuranceMetaData,
  useUpsertInsurances,
} from 'views/EMR/components/Insurance/hooks';
import {
  getInsuranceSlotConflictValidator,
} from 'utils/misc';
import {
  getDemographicsFromInsurance,
} from 'pages/Dashboard/utils/patient';
import InsuranceInfo from 'views/EMR/components/Insurance/InsuranceCard/InsuranceInfo';
import InsuranceHeader from 'views/EMR/components/Insurance/InsuranceCard/InsuranceHeader';
import InsuranceAttachments from 'views/EMR/components/Insurance/InsuranceCard/InsuranceAttachments';
import {
  insurancesEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import isNil from 'lodash/isNil';
import {
  formatDateOnly,
} from 'utils/date';
import {
  getDefaultInactiveEffectiveTill,
} from 'views/EMR/components/Insurance/utils';
import {
  getInsuranceDroppableId,
  InsuranceCardDragItem,
} from 'views/EMR/components/Insurance/insuranceCardDnD';

type RegisterInsuranceRefetch = (
  insuranceId: number,
  refetch: () => Promise<unknown>,
) => void;

type RegisterContainerItems = (
  containerKey: string,
  items: InsuranceCardDragItem[],
) => void;

type Props = {
  patientDemographics: Demographics;
  index: number;
  patient: PatientDTO;
  insurances: InsuranceDTO[];
  activeDragSourceKey: string | null;
  busyContainerKeys: string[];
  isDraggingInsuranceCard: boolean;
  shouldShowDragHint: boolean;
  registerContainerItems: RegisterContainerItems;
  registerInsuranceRefetch: RegisterInsuranceRefetch;
  unregisterContainerItems: (containerKey: string) => void;
  unregisterInsuranceRefetch: (insuranceId: number) => void;
};

export default function InsuranceCard({
  patientDemographics,
  index,
  patient,
  insurances,
  activeDragSourceKey,
  busyContainerKeys,
  isDraggingInsuranceCard,
  shouldShowDragHint,
  registerContainerItems,
  registerInsuranceRefetch,
  unregisterContainerItems,
  unregisterInsuranceRefetch,
}: Props) {
  const notify = useNotify();
  const insurance = insurances[index];
  const droppableId = useMemo(
    () => getInsuranceDroppableId(insurance?.insuranceId ?? 0),
    [insurance?.insuranceId],
  );
  const [updatingActiveState, setUpdatingActiveState] = useState<boolean>(false);
  const [updatingInsuranceSlot, setUpdatingInsuranceSlot] = useState<boolean>(false);

  const {
    relationshipTypesByName,
    insuranceSlotOptions,
    restrictedSlotSet,
    getInsuranceSlotLabel,
  } = useInsuranceMetaData();
  const hasConflictingInsuranceSlot = useMemo(
    () => getInsuranceSlotConflictValidator(
      insurances,
      restrictedSlotSet,
    ),
    [insurances, restrictedSlotSet],
  );

  const {
    isSubscriberPatient,
    isEmployerInsurance,
  } = relationshipTypesByName[insurance.insuranceOwnerRelationshipType ?? ''] ?? {};

  const demographics = isSubscriberPatient || isEmployerInsurance
    ? patientDemographics
    : getDemographicsFromInsurance(insurance);

  const { confirm, closeConfirm } = useConfirmDialog();
  const {
    mutateAsync: deleteInsurance,
    isPending: deletingInsurance,
  } = useDeleteInsurance();

  const {
    mutateAsync: updateInsurance,
    isPending: updatingInsurance,
  } = useUpsertInsurances();

  const onDeleteInsurance = useCallback(async () => {
    confirm({
      content: (
        <div>
          {'Are you sure you want to delete '}
          <span className="font-semibold">
            {insurance?.insuranceName}
          </span>
          ?
        </div>
      ),
      onConfirm: async () => {
        try {
          await deleteInsurance(insurance?.insuranceId ?? 0);
          closeConfirm();
          notify({
            variant: 'success',
            message: 'Insurance deleted successfully',
          });
          insurancesEventChannel.emit('onPatientInsuranceUpdated', patient?.patientId ?? 0, insurance as InsuranceDTO);
        } catch (error) {
          console.error(error);
        }
      },
      title: 'Delete Insurance',
      confirmText: 'Confirm',
    });
  }, [insurances, index]);

  const onUpdateInsurance = useCallback(async (insurance: InsuranceDTO) => {
    await updateInsurance({
      patientId: patient?.patientId ?? 0,
      insurance,
    });
    insurancesEventChannel.emit('onPatientInsuranceUpdated', patient?.patientId ?? 0, insurance as InsuranceDTO);
    notify({
      variant: 'success',
      message: 'Insurance updated successfully',
    });
  }, [patient?.patientId]);

  const onInsuranceSlotChanged = useCallback(async (insuranceSlot: InsuranceSlot) => {
    const updatedInsurance = { ...insurance, insuranceSlot };
    const slotLabel = getInsuranceSlotLabel(insuranceSlot);
    const conflictingInsurance = hasConflictingInsuranceSlot({
      currentInsuranceId: insurance?.insuranceId,
      selectedInsuranceSlot: insuranceSlot,
      isActiveInsurance: !insurance?.inactive,
    });

    if (!isNil(conflictingInsurance)) {
      confirm({
        content: (
          <div>
            Are you sure you want to mark
            {' '}
            <span className="font-semibold">{insurance?.insuranceName}</span>
            {' '}
            as
            {' '}
            <span className="font-semibold">{`Active ${slotLabel}`}</span>
            ?
            <div className="mt-2">
              <span className="font-semibold">{conflictingInsurance.insuranceName}</span>
              {' is currently set as '}
              <span className="font-semibold">{slotLabel}</span>
              {' and will be moved to '}
              <span className="font-semibold">Other</span>
              .
            </div>
          </div>
        ),
        onConfirm: async () => {
          try {
            setUpdatingInsuranceSlot(true);
            await onUpdateInsurance(updatedInsurance);
            closeConfirm();
          } catch (error) {
            console.error(error);
          } finally {
            setUpdatingInsuranceSlot(false);
          }
        },
        title: 'Update Insurance',
        confirmText: 'Confirm',
      });
      return;
    }

    try {
      setUpdatingInsuranceSlot(true);
      await onUpdateInsurance(updatedInsurance);
    } catch (error) {
      console.error(error);
    } finally {
      setUpdatingInsuranceSlot(false);
    }
  }, [onUpdateInsurance, insurance, insuranceSlotOptions, hasConflictingInsuranceSlot]);

  const insuranceStatus = insurance?.inactive ? 'Inactive' : 'Active';

  const saveInsuranceUpdate = useCallback(async (
    updatedInsurance: InsuranceDTO,
    isActivating: boolean,
  ) => {
    const conflictingInsurance = isActivating
      ? hasConflictingInsuranceSlot({
        currentInsuranceId: updatedInsurance?.insuranceId,
        selectedInsuranceSlot: updatedInsurance?.insuranceSlot,
        isActiveInsurance: true,
      })
      : null;

    if (!isNil(conflictingInsurance)) {
      const slotLabel = updatedInsurance?.insuranceSlot
        ? getInsuranceSlotLabel(updatedInsurance.insuranceSlot)
        : undefined;

      confirm({
        content: (
          <div>
            Are you sure you want to mark
            {' '}
            <span className="font-semibold">{updatedInsurance?.insuranceName}</span>
            {' '}
            as
            {' '}
            <span className="font-semibold">{`Active ${slotLabel}`}</span>
            ?
            <div className="mt-2">
              <span className="font-semibold">{conflictingInsurance.insuranceName}</span>
              {' is currently set as '}
              <span className="font-semibold">{slotLabel}</span>
              {' and will be moved to '}
              <span className="font-semibold">Other</span>
              .
            </div>
          </div>
        ),
        onConfirm: async () => {
          try {
            setUpdatingActiveState(true);
            await onUpdateInsurance(updatedInsurance);
            closeConfirm();
          } catch (error) {
            console.error(error);
          } finally {
            setUpdatingActiveState(false);
          }
        },
        title: 'Update Insurance',
        confirmText: 'Confirm',
      });
      return;
    }

    try {
      setUpdatingActiveState(true);
      await onUpdateInsurance(updatedInsurance);
    } catch (error) {
      console.error(error);
    } finally {
      setUpdatingActiveState(false);
    }
  }, [confirm, closeConfirm, getInsuranceSlotLabel, hasConflictingInsuranceSlot, onUpdateInsurance]);

  const onActiveToggled = useCallback(async (isActive: boolean) => {
    const updatedInsurance = isActive
      ? {
        ...insurance,
        inactive: false,
        effectiveTill: null,
      }
      : {
        ...insurance,
        inactive: true,
        effectiveTill: formatDateOnly(getDefaultInactiveEffectiveTill()),
      };
    await saveInsuranceUpdate(updatedInsurance, isActive);
  }, [insurance, saveInsuranceUpdate]);

  return (
    <div className="w-[calc(50%-12px)] insurance-card flex flex-col gap-2 border border-blue-100 rounded-md shadow-sm min-w-[350px]">
      <InsuranceHeader
        insurance={insurance}
        insuranceStatus={insuranceStatus}
        onInsuranceSlotChanged={onInsuranceSlotChanged}
        updatingInsuranceSlot={updatingInsuranceSlot}
        onActiveToggled={onActiveToggled}
        updatingActiveStatus={updatingActiveState}
        onOpenInsuranceDialog={() => {
          insurancesEventChannel.emit('openInsuranceDialog', {
            insurance,
            patientId: patient?.patientId ?? 0,
          });
        }}
        onDeleteInsurance={onDeleteInsurance}
        isDeleting={deletingInsurance}
      />
      <InsuranceInfo
        {...insurance}
        demographics={demographics as Demographics}
        patient={patient}
        updatingInsurances={updatingInsurance || updatingInsuranceSlot || updatingActiveState}
      />
      <InsuranceAttachments
        insuranceId={insurance?.insuranceId}
        patientId={patient?.patientId}
        activeDragSourceKey={activeDragSourceKey}
        droppableId={droppableId}
        busyContainerKeys={busyContainerKeys}
        isDraggingInsuranceCard={isDraggingInsuranceCard}
        shouldShowDragHint={shouldShowDragHint}
        registerContainerItems={registerContainerItems}
        registerRefetch={registerInsuranceRefetch}
        unregisterContainerItems={unregisterContainerItems}
        unregisterRefetch={unregisterInsuranceRefetch}
      />
    </div>
  );
}
